import { useAuth, useUser } from "@clerk/react";
import * as Sentry from "@sentry/react";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import dayjs from "dayjs";
import { usePostHog } from "posthog-js/react";
import type { ReactNode } from "react";
import z from "zod";
import { AppShell } from "@/components/app-shell";
import {
  getAccount,
  getCurrentOrganization,
  getEmails,
  getGetAccountQueryKey,
  getGetCurrentOrganizationQueryKey,
  getGetEmailsQueryKey,
  getGetPausesQueryKey,
  getGetToNotifyQueryKey,
  getPauses,
  getStatusQueryKey,
  getToNotify,
  linkOrganizations,
  status,
} from "@/models/backend";
import { queryClient } from "@/helpers/tanstack-client";
import { client } from "@/models/client";

const globalSchema = z.object({
  organizationId: z.string().optional(),
  link: z.string().optional(),
  ...(import.meta.env.VITE_IS_BYPASS ? { activeOrg: z.string().optional() } : {}),
});

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: async ({ context, search, location }) => {
    if (context.auth.isSignedIn == false) {
      throw redirect({
        to: "/signin",
        search: {
          redirect_url: window.location.pathname + window.location.search.toString(),
        },
      });
    }
    if (
      context.session &&
      dayjs(context.session?.createdAt).startOf("day").isBefore(dayjs()) &&
      context.session?.expireAt &&
      dayjs(context.session?.expireAt).startOf("day").isBefore(dayjs())
    ) {
      context.auth.signOut();
    }
    if (!context.auth.orgId) {
      throw redirect({
        to: "/onboarding/organization",
        search: {
          redirect_url: window.location.pathname + window.location.search.toString(),
        },
      });
    }
    client.interceptors.request.use(async (config) => {
      if (import.meta.env.VITE_IS_BYPASS && search.activeOrg) {
        config.headers["X-OrganizationId"] = search.activeOrg;
      }
      const accessToken = await context.auth.getToken();
      config.headers["Authorization"] = `Bearer ${accessToken}`;
      return config;
    });
    if (search.activeOrg) {
      return;
    }
    if (search.link) {
      let linkedOrgId: string;
      try {
        linkedOrgId = await linkOrganizations({ parent_id: search.link });
      } catch (e) {
        Sentry.captureException(e);
        console.error(e);
        throw redirect({ to: "/dashboard" });
      }
      try {
        await context.clerk.setActive({ organization: linkedOrgId });
        await context.auth.getToken({ skipCache: true });
      } catch (e) {
        Sentry.captureException(e);
        throw redirect({ to: "/dashboard" });
      }
      queryClient.clear();
      throw redirect({
        to: "/admin/organizations",
        replace: true,
      });
    } else if (search.organizationId) {
      if (search.organizationId !== context.auth.orgId) {
        try {
          await context.clerk.setActive({ organization: search.organizationId });
          await context.auth.getToken({ skipCache: true });
        } catch (e) {
          Sentry.captureException(e);
          throw redirect({ to: "/dashboard" });
        }
        queryClient.clear();
      }
      const { organizationId: _switchedTo, ...rest } = search;
      throw redirect({ to: location.pathname, search: rest, replace: true });
    }
  },
  loader: async () => {
    await Promise.all([
      queryClient.ensureQueryData({
        queryFn: getCurrentOrganization,
        queryKey: getGetCurrentOrganizationQueryKey(),
      }),
      queryClient.ensureQueryData({
        queryFn: getToNotify,
        queryKey: getGetToNotifyQueryKey(),
      }),
      queryClient.ensureQueryData({
        queryFn: getAccount,
        queryKey: getGetAccountQueryKey(),
      }),
      queryClient.ensureQueryData({
        queryFn: getPauses,
        queryKey: getGetPausesQueryKey(),
      }),
      queryClient.ensureQueryData({
        queryFn: status,
        queryKey: getStatusQueryKey(),
      }),
      queryClient.ensureQueryData({
        queryFn: (opts) => getEmails({ include_users: true }, opts),
        queryKey: getGetEmailsQueryKey(),
      }),
    ]);
  },
  component: RouteComponent,
  validateSearch: globalSchema,
  search: {
    middlewares: [
      ({ search, next }) => {
        const result = next(search);
        if (search.activeOrg) {
          return {
            ...result,
            activeOrg: search.activeOrg,
          };
        }
        return result;
      },
    ],
  },
});

const Main = ({ children }: { children: ReactNode }) => {
  const { userId } = useAuth();
  const { user } = useUser();
  const posthog = usePostHog();

  if (userId) {
    posthog.identify(userId, {
      user_id: userId,
      first_name: user?.firstName,
      last_name: user?.lastName,
      email: user?.emailAddresses?.[0]?.emailAddress,
    });
  }

  return <>{children}</>;
};

function RouteComponent() {
  return (
    <AppShell>
      <Main>
        <Outlet />
      </Main>
    </AppShell>
  );
}
